/**
 * SizeField — a `size` attribute: a number + a unit picked from the field's
 * `allowed_units` (PIM Wave P2, Task 3). The seller's RAW number + RAW unit are
 * what round-trip and display (locked Q4); the backend derives the canonical
 * standardized value. Per Q4 the client NEVER standardizes, so the canonical
 * unit is surfaced only as an informational note ("stored in <unit>"), not as
 * a live-computed conversion (that conversion table is backend-only).
 */
import type { ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { SELLER_DASHBOARD_NS } from "@/features/seller-dashboard/api";
import {
  pickOptionalText,
  pickText,
  type AttributeFormValues,
} from "@/features/seller-dashboard/attributes/formSchema";
import {
  ATTR_INPUT_CLASS,
  ATTR_INPUT_ERROR_CLASS,
  FieldShell,
  type AttributeFieldProps,
} from "@/features/seller-dashboard/attributes/fields/FieldShell";

export interface SizeFieldProps extends AttributeFieldProps {
  /** The current unit value (RHF key `unitKey(field_slug)`). */
  unit: string;
  onUnitChange: (unit: string) => void;
  onUnitBlur?: () => void;
  /** i18n error KEY for a unit-specific problem (missing / not allowed). */
  unitError?: string;
}

/** Convenience: read both halves of a size field out of the flat form values. */
export function readSize(
  values: AttributeFormValues,
  fieldSlug: string,
  unitFieldKey: string,
): { value: string; unit: string } {
  return { value: values[fieldSlug] ?? "", unit: values[unitFieldKey] ?? "" };
}

export function SizeField({
  field,
  language,
  value,
  onChange,
  onBlur,
  error,
  unit,
  onUnitChange,
  onUnitBlur,
  unitError,
  disabled,
}: SizeFieldProps): ReactElement {
  const { t } = useTranslation(SELLER_DASHBOARD_NS);
  const label = pickText(field.label, language);
  const hint = pickOptionalText(field.hint, language);
  const example = pickOptionalText(field.example, language);
  const controlId = `attr-${field.field_slug}`;
  const allowedUnits = field.allowed_units ?? [];
  return (
    <FieldShell
      slug={field.field_slug}
      label={label}
      requirement={field.requirement}
      hint={hint}
      error={error ?? unitError}
    >
      <div className="flex gap-2">
        <input
          id={controlId}
          type="number"
          inputMode="decimal"
          step="any"
          aria-label={label}
          aria-invalid={error ? true : undefined}
          placeholder={example}
          value={value}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          className={["flex-1", ATTR_INPUT_CLASS, error ? ATTR_INPUT_ERROR_CLASS : ""].join(
            " ",
          )}
        />
        <select
          aria-label={t("attributes.size.unitLabel")}
          aria-invalid={unitError ? true : undefined}
          value={unit}
          disabled={disabled}
          onChange={(e) => onUnitChange(e.target.value)}
          onBlur={onUnitBlur}
          className={[
            "w-28 shrink-0",
            ATTR_INPUT_CLASS,
            unitError ? ATTR_INPUT_ERROR_CLASS : "",
          ].join(" ")}
        >
          <option value="">{t("attributes.size.unitPlaceholder")}</option>
          {allowedUnits.map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
        </select>
      </div>
      {field.canonical_unit ? (
        <p className="text-xs text-muted-foreground">
          {t("attributes.size.canonicalNote", { unit: field.canonical_unit })}
        </p>
      ) : null}
    </FieldShell>
  );
}

export default SizeField;
